/**
 *
 * FBDocuments
 *
 */

import React, { memo, useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// styles
import './style.scss';

// antd component
import { Form } from 'antd';

// icons
//import CustomIcons from 'components/CustomIcons';
//import { ExclamationCircleOutlined } from '@ant-design/icons';

// components
import ErrorBoundary from 'engine/ErrorBoundary';
import HiddenField from 'components/Forms/Fields/HiddenField';

// Communication components
import {
  FBSectionHeader,
  FBSectionErroMessage,
} from 'components/Communications';
// inner components
import FileRow from './components/FileRow';
import UploadFileButton from './components/UploadFileButton';

// hooks
import { useRefState } from 'utils/hooks';
import { STORAGE_BUCKET_URL } from 'utils/constants.js';


function FBDocuments({
  name,
  mode,
  method,
  initialValues,
  isValidSection,
  // actions
  setValidSection,
  onDelete,
  onChangeSection,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames('tcf-fb-documents-group', 'tcf-fb-group',  className);

  const [docsForm] = Form.useForm();

  const [validAmountOfFields, setValidAmountOfFields] = useState(isValidSection || false);
  const [currentValues, currentValuesRef, setCurrentValues] = useRefState(initialValues || {});
  
  useEffect(() => {
    if (!!initialValues) {
      const hasValid = Array.isArray(initialValues[`${name}-fields`]) && initialValues[`${name}-fields`]?.length > 0;
      setValidAmountOfFields(hasValid);
      !!setValidSection && setValidSection(name, hasValid);
      if (JSON.stringify(initialValues) != JSON.stringify(currentValues)) {
        setCurrentValues(initialValues);
      }
    } else if (!!!initialValues) {
      resetAllStates();
    }

    return () => {
      resetAllStates();
    };
  }, []);

  const resetAllStates = useCallback(() => {
    setCurrentValues({});
    setValidAmountOfFields(false);
    docsForm.resetFields();
  }, [initialValues, currentValues]);

  // Delete Section
  const deleteSection = useCallback(
    name => {
      resetAllStates();
      onChangeSectionHandler(null);
      !!onDelete && onDelete(name);
    },
    [name],
  );

  // update Section
  const onChangeSectionHandler = useCallback(
    sectionData => {
      !!onChangeSection && onChangeSection(name, sectionData);
    },
    [currentValues, onChangeSection],
  );

  // custom Validation for Section fileds
  const checkValidCallback = useCallback(async (rule, values, callback) => {
      let hasValid = false;
      if (!!values && Array.isArray(values) && values?.length > 0) {
        hasValid = true;
      } else {
        hasValid = false;
      }

      setValidAmountOfFields(hasValid);
      !!setValidSection && setValidSection(name, hasValid);

      if (hasValid) {
        return Promise.resolve(hasValid);
      } else {
        return Promise.reject(new Error("L'entrée n'est pas valide."), values);
      }

    }, [setValidSection]);

  // Add row
  const onAddNewMedia = useCallback(
    (data, callbackAdd) => {
      if (data?.length > 0) {
        const fileUrl = data[0];
        const fileName = fileUrl.replace(STORAGE_BUCKET_URL + '/', '');
        const newValue = {
          'file-name': fileName,
          'file-url': fileUrl,
        };

        const filedsValues = currentValues[`${name}-fields`] || [];
        const hasNewData = filedsValues?.length > 0 && filedsValues?.some(
          file => file['file-url'] == fileUrl,
        );
        
        if (!!!hasNewData || filedsValues?.length == 0) {
          !!callbackAdd && callbackAdd(newValue);
          setValidAmountOfFields(true);
        }
      }
    },
    [currentValues],
  );
  // Delete Row
  const onRemoveRow = useCallback(
    (index, callbackRemove) => {
      let values = currentValues || {};
      let filedsValues = currentValues[`${name}-fields`] || [];
    
      if (filedsValues?.length > 0) {
        filedsValues = filedsValues?.filter((el, inx) => inx != index) || filedsValues;
        values = {
          ...values,
          [`${name}-fields`]: filedsValues
        }
        setCurrentValues(values);
        docsForm.setFieldsValue(values);
        if (filedsValues?.length == 0) {
          setValidAmountOfFields(false);
        }
        !!callbackRemove && callbackRemove(index);
      } else {
        values = {
          ...values,
          [`${name}-fields`]: []
        }
        setCurrentValues(values);
        docsForm.setFieldsValue(values);
        setValidAmountOfFields(false);
      }
    },
    [currentValues, currentValuesRef, docsForm],
  );

  const erroMessage = useMemo(() => (
    <FBSectionErroMessage
      showMessage={!validAmountOfFields}
      message="Cette section nécessite le chargement d'un ou
      plusieurs fichiers."
    />
  ), [validAmountOfFields, isValidSection]);

  // Update parent states based on changes inside Form
  const handleValuesChange = useCallback((changedValues, allValues) => {
    let values = allValues[name] || {};
    values = { ...values, ...changedValues[name] };
    setCurrentValues(values);

   // docsForm.validateFields();
  }, [name, currentValues]);
  
  return name && (
    <div className={childClassNames}>
      <Form
        name={name}
        form={docsForm}
        initialValues={{[name]: currentValues}}
        className="tcf-form-builder"
        autoComplete="off"
        onValuesChange={handleValuesChange}
      >
        <FBSectionHeader type="documents" onDelete={() => deleteSection(name)} />
        <div className="tcf-fb-group">

          <HiddenField
            key={[name, `${name}-title`]}
            data={{ name: [name, `${name}-title`] }}
            index={0}
          />
          <HiddenField
            key={[name, `${name}-description`]}
            data={{ name: [name, `${name}-description`] }}
            index={0}
          />

          <ErrorBoundary>
            <Form.List
              className="tcf-fb-group"
              name={[name, `${name}-fields`]}
            //  validateTrigger="onChange"
              rules={[
                {
                  required: true,
                  validator: checkValidCallback,
                  validateTrigger: 'onChange',
                  message: 'Champ obligatoire',
                },
              ]}
            >
              {(fieldsChild, { add, remove, move }) => (
                  <>
                    {fieldsChild?.map((element, index) => {
                      return (
                        <FileRow
                          key={`${name}-fields-`+index}
                          nameRow={`${name}-fields`}
                          element={element}
                          rowsValues={currentValues[`${name}-fields`] || []}
                          onRemove={name => onRemoveRow(name, remove)}
                        />
                      );
                    })}
                  
                    <UploadFileButton
                        onAdd={data => onAddNewMedia(data, add)}
                    />

                  </>
                )}
            </Form.List>
          </ErrorBoundary>

          {erroMessage}

        </div>
      </Form>
    </div>
  );
}

FBDocuments.defaultProps = {
  name: 'documents',
  mode: 'new',
  method: 'nouvel',
};
FBDocuments.propTypes = {
  onDelete: PropTypes.func,
  onChangeSection: PropTypes.func,
};

export default memo(FBDocuments);
